import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { WorkClient } from "@/features/warehouse/WorkClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";

// Warehouse → Werk (docs/design_handoff_wms_mw8/README.md §5, BO-Werk): the team leader's overview. Reading
// is the stock or the orders.
export const Route = createFileRoute("/_shell/warehouse/work")({
  beforeLoad: ({ context }) => {
    const may = ["wms_stock", "wms_stock_write", "wms_outbound", "wms_outbound_write"] as const;
    if (!may.some((key) => hasModule(context.session, key))) throw redirect({ to: firstAllowedRoute(context.session) });
  },
  validateSearch: (search: Record<string, unknown>): { warehouse?: string } => ({
    warehouse: typeof search.warehouse === "string" && search.warehouse ? search.warehouse : undefined,
  }),
  component: WorkScreen,
});

function WorkScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );
  const choice = useWarehouseChoice(search.warehouse, (code) => updateParams({ warehouse: code }));
  const warehouse = choice.current;

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Work">
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <WorkClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          userId={session.userId}
          canUnpick={hasModule(session, "wms_outbound_write")}
        />
      )}
    </div>
  );
}
